import { useMemo } from 'react'
import dayjs from 'dayjs'

import { usePersistentObservatoryPageStore } from '@/feature/observatory'
import { useFlag } from '@/feature/feature-flags'
import { useTasksBoardAll } from '@/feature/execution-board'
import {
  LIVE_TASK_STATUSES,
  SCHEDULED_TASK_STATUSES,
} from '@/feature/execution-board/utils/taskMapVisibility'

export const useAreTasksEnabled = () => {
  // It's all going to get release as part of execution board (so lives under that flag)
  return useFlag('featureExecutionBoardUi', false)
}

// The task statuses fetched for the map, driven by the layer toggles. Statuses come from the
// shared taskMapVisibility source so the layer and the board's "View on map" gate never diverge.
export const getTaskStatusesFilter = (liveTasks?: boolean, scheduledTasks?: boolean): string[] => {
  const stats: string[] = []
  if (liveTasks) stats.push(...LIVE_TASK_STATUSES)
  if (scheduledTasks) stats.push(...SCHEDULED_TASK_STATUSES)
  return stats
}

export const useObservatoryTasks = () => {
  const toggles = usePersistentObservatoryPageStore((state) => state.toggles)
  const isEnabled = useAreTasksEnabled()

  const statusesFilter = useMemo(
    () => getTaskStatusesFilter(toggles.liveTasks, toggles.scheduledTasks),
    [toggles.liveTasks, toggles.scheduledTasks],
  )

  const dateRangeFilter = useMemo(() => {
    if (toggles.scheduledTasks) {
      // Scheduled to show only the next 7 days Tasks
      return {
        startDate: dayjs().toDate(),
        endDate: dayjs().add(7, 'day').toDate(),
      }
    }
    return null
  }, [toggles.scheduledTasks])

  // The task set shown on the map is driven purely by the layer toggles — NOT by focus state.
  // Resolving a focused task that isn't in this set is handled separately by useFocusedTask, so
  // that focusing/unfocusing never changes which task markers or counts the layer renders.
  const filters = useMemo(() => {
    if (!isEnabled || statusesFilter.length === 0) return null

    return {
      pageSize: Number.MAX_SAFE_INTEGER,
      status: statusesFilter,
      ...dateRangeFilter,
    }
  }, [isEnabled, statusesFilter, dateRangeFilter])

  return useTasksBoardAll(filters)
}
